import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, PartyPopper, AlertCircle } from "lucide-react";
import { getPaymentStatus, confirmBooking } from "@/lib/api";

const getSessionId = () => new URLSearchParams(window.location.search).get("session_id");

export const PaymentSuccess = () => {
  const [state, setState] = useState("checking"); // checking | done | failed
  const [reference, setReference] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = getSessionId();
    const pending = JSON.parse(localStorage.getItem("sadara_pending") || "null");
    if (!sessionId) { setState("failed"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const s = await getPaymentStatus(sessionId);
        if (s.payment_status === "paid") {
          const res = await confirmBooking({
            itinerary_id: s.itinerary_id || (pending && pending.itinerary_id),
            payment_method: "Payment Link (Card)",
            session_id: sessionId,
            guest_name: pending && pending.guest_name,
            guest_email: pending && pending.guest_email,
            guest_phone: pending && pending.guest_phone,
          });
          localStorage.removeItem("sadara_pending");
          setReference(res.reference);
          setState("done");
          return;
        }
        if (["failed", "expired"].includes(s.payment_status) || attempts.current > 10) {
          setState("failed");
          return;
        }
      } catch (e) {
        if (attempts.current > 10) { setState("failed"); return; }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="h-screen w-screen sadara-bg grain flex items-center justify-center px-4" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl max-w-md w-full p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-gold mx-auto animate-spin" />
            <h1 className="font-display text-2xl mt-4">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">One moment while Isack finalises everything.</p>
          </>
        )}
        {state === "done" && (
          <div data-testid="success-confirmed">
            <PartyPopper className="w-12 h-12 text-gold mx-auto" />
            <h1 className="font-display text-3xl mt-4">Karibu — you're all set!</h1>
            <p className="text-sm text-muted-foreground mt-3">
              Booking reference <span className="gold-text font-semibold">{reference}</span>
            </p>
            <p className="text-sm text-muted-foreground mt-2">Your confirmation is on its way by email &amp; WhatsApp. Asante sana.</p>
            <Link to="/" className="inline-block mt-6 rounded-full bg-gold px-7 py-3 text-sm font-semibold text-background hover:bg-gold-soft transition-colors" data-testid="back-home-btn">
              Back to Isack
            </Link>
          </div>
        )}
        {state === "failed" && (
          <>
            <AlertCircle className="w-10 h-10 text-gold mx-auto" />
            <h1 className="font-display text-2xl mt-4">We couldn't confirm that payment</h1>
            <p className="text-sm text-muted-foreground mt-2">No charge was completed. Please return and try again — Isack is waiting.</p>
            <Link to="/" className="inline-block mt-6 rounded-full border border-gold/40 px-7 py-3 text-sm text-gold hover:bg-gold/10 transition-colors" data-testid="back-home-btn">
              Back to Isack
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};
